/**
 * Custom Gift Card page logic.
 *
 * Two delivery paths, two very different mechanisms:
 *
 *  - "Send now": adds the REAL gift card product's variant to cart with
 *    Shopify's own reserved recipient properties. 100% native — Shopify
 *    emails the recipient itself as soon as the order is paid. No backend.
 *
 *  - "Schedule" (exact date + time + timezone): Shopify's native recipient
 *    form only supports a plain date, delivered at a time Shopify chooses —
 *    there's no native way to pick an exact hour. So this path instead adds
 *    the *scheduled-delivery placeholder product*'s matching-priced variant
 *    to cart (a plain product, not gift_card type) carrying the recipient
 *    info and the exact delivery instant (converted to UTC) as custom cart
 *    properties. The scheduled-gift-card-backend service picks this order up
 *    via webhook once paid, waits until that instant, then creates the real
 *    gift card via the Admin API and emails the recipient itself.
 *
 *    Cart property names matter here: Shopify hides any property whose name
 *    starts with "_" from the cart/checkout summary shown to the customer.
 *    "Recipient name"/"Recipient email"/"Message"/"Delivery date" are named
 *    without the underscore on purpose, so the buyer sees the same
 *    confirmation on this path as they do on "Send now". "_scheduled_for_utc"
 *    and "_send_to_recipient" stay underscored -- they're backend-only.
 *
 *  - "Custom amount" (only shown when the section has a backend URL
 *    configured): no Shopify checkout can charge a customer-typed price
 *    directly, so this path never touches /cart/add.js at all. Instead it
 *    POSTs straight to the scheduled-gift-card-backend service, which
 *    creates a Shopify Draft Order for that exact amount (via
 *    `priceOverride`) and hands back a real Shopify-hosted invoice/checkout
 *    URL to redirect to. Works combined with either "Send now" or
 *    "Schedule" -- the schedule date/time fields are reused as-is and just
 *    get sent to the backend instead of going into cart properties.
 */
(function () {
  'use strict';

  document.querySelectorAll('[data-cgc]').forEach(initCustomGiftCard);
  document.querySelectorAll('[data-cgc-slideshow]').forEach(initSlideshow);

  /**
   * Media slideshow: one slide visible at a time, prev/next arrows + dots.
   * Video/external-video slides are the theme's own <deferred-media> element
   * (same one Dawn's product page uses) -- they show a poster image with a
   * play button and only load/play the actual video once clicked, so
   * clicking a video slide plays it in place rather than just showing a
   * static frame.
   */
  function initSlideshow(root) {
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-cgc-slide]'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-cgc-slide-dot]'));
    var prevBtn = root.querySelector('[data-cgc-slide-prev]');
    var nextBtn = root.querySelector('[data-cgc-slide-next]');

    if (slides.length < 2) return;

    var current = 0;

    function goTo(index) {
      if (index === current) return;

      // Stop any playing video/audio on the slide we're leaving.
      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();

      current = (index + slides.length) % slides.length;

      slides.forEach(function (slide, i) {
        slide.classList.toggle('is-active', i === current);
      });
      dots.forEach(function (dot, i) {
        dot.classList.toggle('is-active', i === current);
        dot.setAttribute('aria-pressed', i === current ? 'true' : 'false');
      });
    }

    if (prevBtn) prevBtn.addEventListener('click', function () { goTo(current - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goTo(current + 1); });
    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () { goTo(i); });
    });
  }

  function initCustomGiftCard(root) {
    var form = root.querySelector('[data-cgc-form]');
    if (!form) return;

    var scheduledVariants = [];
    try {
      scheduledVariants = JSON.parse(root.dataset.scheduledVariants || '[]');
    } catch (e) {
      scheduledVariants = [];
    }

    var backendBaseUrl = (root.dataset.backendBaseUrl || '').trim().replace(/\/$/, '');
    var placeholderVariantId = root.dataset.placeholderVariantId || '';

    var state = {
      variantId: null,
      price: null,
      giftMode: true, // true = "Send as a gift card", false = "Send it to me"
      scheduleMode: false, // true = "Schedule" (exact date/time)
      amountMode: 'fixed' // 'fixed' = pick a denomination, 'custom' = type any amount
    };

    var amountButtons = Array.prototype.slice.call(root.querySelectorAll('[data-cgc-amount-option]'));
    var amountTabs = Array.prototype.slice.call(root.querySelectorAll('[data-cgc-amount-tab]'));
    var amountGrid = root.querySelector('[data-cgc-amount-grid]');
    var customAmountWrap = root.querySelector('[data-cgc-custom-amount]');
    var customAmountInput = form.querySelector('[name="custom_amount"]');

    var giftTabs = Array.prototype.slice.call(root.querySelectorAll('[data-cgc-gift-tab]'));
    var recipientFields = root.querySelector('[data-cgc-recipient-fields]');

    var scheduleTabs = Array.prototype.slice.call(root.querySelectorAll('[data-cgc-schedule-tab]'));
    var sendOnWrap = root.querySelector('[data-cgc-send-on]');

    var submitBtn = root.querySelector('[data-cgc-submit]');
    var submitLabel = root.querySelector('[data-cgc-submit-label]');

    // Live card preview: amount + recipient name overlaid on the card
    // design image (only present once a "Gift card design image" is set
    // in the section settings).
    var cardAmountEl = root.querySelector('[data-cgc-card-amount]');
    var cardNameEl = root.querySelector('[data-cgc-card-name]');
    var recipientNameInput = form.querySelector('[name="recipient_name"]');

    if (recipientNameInput && cardNameEl) {
      var namePlaceholder = cardNameEl.dataset.placeholder || cardNameEl.textContent;
      recipientNameInput.addEventListener('input', function () {
        cardNameEl.textContent = recipientNameInput.value.trim() || namePlaceholder;
      });
    }

    if (amountButtons.length) {
      state.variantId = amountButtons[0].dataset.variantId;
      state.price = amountButtons[0].dataset.price;
    }

    amountButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        amountButtons.forEach(function (b) {
          b.classList.remove('is-active');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');
        state.variantId = btn.dataset.variantId;
        state.price = btn.dataset.price;
        if (cardAmountEl) cardAmountEl.textContent = btn.textContent;
        clearError('form');
      });
    });

    // Currency symbol lifted from a fixed-amount button (e.g. "₹" out of
    // "₹2,500.00") so the custom-amount live preview can be prefixed with
    // it too, without needing a separate money-formatting dependency.
    var currencySymbol = amountButtons.length ? (/^[^\d]*/.exec(amountButtons[0].textContent)[0] || '').trim() : '';

    amountTabs.forEach(function (btn) {
      btn.addEventListener('click', function () {
        amountTabs.forEach(function (b) {
          b.classList.remove('is-active');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');
        state.amountMode = btn.dataset.cgcAmountTab === 'custom' ? 'custom' : 'fixed';
        if (amountGrid) amountGrid.hidden = state.amountMode === 'custom';
        if (customAmountWrap) customAmountWrap.hidden = state.amountMode !== 'custom';
        clearError('form');
        clearError('custom_amount');
        updateCardPreviewAmount();
      });
    });

    if (customAmountInput) {
      customAmountInput.addEventListener('input', function () {
        clearError('custom_amount');
        updateCardPreviewAmount();
      });
    }

    function updateCardPreviewAmount() {
      if (!cardAmountEl) return;
      if (state.amountMode === 'custom') {
        var raw = customAmountInput ? customAmountInput.value.trim() : '';
        cardAmountEl.textContent = raw ? (currencySymbol + raw) : cardAmountEl.textContent;
      } else {
        var activeBtn = amountButtons.filter(function (b) { return b.classList.contains('is-active'); })[0];
        if (activeBtn) cardAmountEl.textContent = activeBtn.textContent;
      }
    }

    giftTabs.forEach(function (btn) {
      btn.addEventListener('click', function () {
        giftTabs.forEach(function (b) {
          b.classList.remove('is-active');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');
        state.giftMode = btn.dataset.cgcGiftTab === 'gift';
        if (recipientFields) recipientFields.style.display = state.giftMode ? '' : 'none';
        clearError('recipient_email');
      });
    });

    scheduleTabs.forEach(function (btn) {
      btn.addEventListener('click', function () {
        scheduleTabs.forEach(function (b) {
          b.classList.remove('is-active');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('is-active');
        btn.setAttribute('aria-pressed', 'true');
        state.scheduleMode = btn.dataset.cgcScheduleTab === 'schedule';
        if (sendOnWrap) sendOnWrap.hidden = !state.scheduleMode;
        clearError('send_on');
      });
    });

    form.addEventListener('submit', function (evt) {
      evt.preventDefault();
      clearAllErrors();

      // Honeypot — if filled, silently drop (bot).
      var hp = form.querySelector('.cgc__hp');
      if (hp && hp.value) return;

      var recipientName = fieldValue('recipient_name');
      var recipientEmail = fieldValue('recipient_email');
      var message = fieldValue('message');

      if (state.giftMode && !isValidEmail(recipientEmail)) {
        showError('recipient_email', 'Enter a valid recipient email.');
        return;
      }

      if (state.amountMode === 'custom') {
        if (!backendBaseUrl) {
          showError('form', 'Custom amount isn’t available right now — please choose a fixed amount.');
          return;
        }
        var customAmountValue = customAmountInput ? parseFloat(customAmountInput.value) : NaN;
        if (!isFinite(customAmountValue) || customAmountValue <= 0) {
          showError('custom_amount', 'Enter a valid amount.');
          return;
        }
        submitCustomAmount({
          recipientName: recipientName,
          recipientEmail: recipientEmail,
          message: message,
          amount: customAmountValue
        });
        return;
      }

      if (!state.variantId) {
        showError('form', 'Choose an amount.');
        return;
      }

      if (state.scheduleMode) {
        submitScheduled({ recipientName: recipientName, recipientEmail: recipientEmail, message: message });
      } else {
        submitNow({ recipientName: recipientName, recipientEmail: recipientEmail, message: message });
      }
    });

    function submitNow(data) {
      var properties = {};
      if (state.giftMode) {
        properties['__shopify_send_gift_card_to_recipient'] = 'if_present';
        if (data.recipientEmail) properties['Recipient email'] = data.recipientEmail;
        if (data.recipientName) properties['Recipient name'] = data.recipientName;
        if (data.message) properties['Message'] = data.message;
        properties['__shopify_offset'] = String(new Date().getTimezoneOffset());
      }

      addToCartAndCheckout(state.variantId, properties);
    }

    // Reads + validates the delivery date/time/timezone fields, shared by
    // both the fixed-denomination "Schedule" path and the custom-amount
    // path when it's combined with "Schedule". Shows the relevant error
    // itself and returns null on any problem; returns
    // { utcMs, display } on success.
    function readScheduleInput() {
      var dtInput = form.querySelector('[name="delivery_datetime"]');
      var tzSelect = form.querySelector('[name="delivery_timezone"]');
      var dtValue = dtInput ? dtInput.value : '';
      var tzOffsetMinutes = tzSelect ? parseInt(tzSelect.value, 10) : 0;
      var tzLabel = tzSelect ? tzSelect.options[tzSelect.selectedIndex].text : '';

      if (!dtValue) {
        showError('send_on', 'Pick a delivery date and time.');
        return null;
      }

      var utcMs = localWallClockToUtcMs(dtValue, tzOffsetMinutes);
      if (utcMs === null || utcMs <= Date.now()) {
        showError('send_on', 'Pick a date and time in the future.');
        return null;
      }

      var maxMs = Date.now() + 90 * 24 * 60 * 60 * 1000;
      if (utcMs > maxMs) {
        showError('send_on', 'Please choose a date within the next 90 days.');
        return null;
      }

      return { utcMs: utcMs, display: formatLocalDisplay(dtValue, tzLabel) };
    }

    function submitScheduled(data) {
      var scheduleInput = readScheduleInput();
      if (!scheduleInput) return;

      var scheduledVariant = scheduledVariants.find(function (v) {
        return String(v.price) === String(state.price);
      });

      if (!scheduledVariant) {
        showError(
          'form',
          'Scheduling isn’t set up for this amount yet — please choose "Send now", or contact us.'
        );
        return;
      }

      // Property names WITHOUT a leading underscore show up on the cart and
      // checkout summary (Shopify hides any property whose name starts with
      // "_" from the customer-facing UI) -- use the same visible names
      // "Send now" uses (Recipient name/email, Message) so both paths look
      // consistent to the buyer, plus a friendly "Delivery date" line so
      // they can confirm what they picked before paying. The underscored
      // ones below are backend-only (raw UTC instant + internal flag) and
      // stay hidden on purpose -- no reason to show a customer a raw ISO
      // timestamp.
      var properties = {
        'Delivery date': scheduleInput.display,
        _scheduled_for_utc: new Date(scheduleInput.utcMs).toISOString(),
        _send_to_recipient: state.giftMode ? 'yes' : 'no'
      };

      if (state.giftMode) {
        if (data.recipientName) properties['Recipient name'] = data.recipientName;
        if (data.recipientEmail) properties['Recipient email'] = data.recipientEmail;
        if (data.message) properties['Message'] = data.message;
      }

      addToCartAndCheckout(scheduledVariant.id, properties);
    }

    // Custom amount never goes through /cart/add.js -- no variant carries
    // that price. Instead the backend creates a Shopify Draft Order for the
    // exact typed amount (via priceOverride) and hands back a real
    // Shopify-hosted invoice URL to pay on. Combines with "Schedule" the
    // same way the fixed-amount path does: the backend stores the same
    // visible/hidden property split on the resulting order's line item.
    function submitCustomAmount(data) {
      var scheduleInput = null;
      if (state.scheduleMode) {
        scheduleInput = readScheduleInput();
        if (!scheduleInput) return;
      }

      if (!placeholderVariantId) {
        showError('form', 'Custom amount isn’t set up yet — please contact us.');
        return;
      }

      var payload = {
        amount: data.amount,
        variantId: placeholderVariantId,
        giftMode: state.giftMode,
        scheduleMode: state.scheduleMode,
        recipientName: state.giftMode ? data.recipientName : '',
        recipientEmail: state.giftMode ? data.recipientEmail : '',
        message: state.giftMode ? data.message : ''
      };

      if (state.scheduleMode) {
        payload.scheduledForUtc = new Date(scheduleInput.utcMs).toISOString();
        payload.scheduledForDisplay = scheduleInput.display;
      }

      setLoading(true);

      fetch(backendBaseUrl + '/draft-orders/gift-card', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (res) {
          return res.json().then(function (json) {
            if (!res.ok) throw json;
            return json;
          });
        })
        .then(function (json) {
          if (!json || !json.invoiceUrl) throw new Error('Missing invoice URL in response.');
          window.location.href = json.invoiceUrl;
        })
        .catch(function (err) {
          setLoading(false);
          showError('form', (err && (err.error || err.description)) || 'Could not start checkout. Please try again.');
        });
    }

    function addToCartAndCheckout(variantId, properties) {
      setLoading(true);

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1, properties: properties }]
        })
      })
        .then(function (res) {
          return res.json().then(function (json) {
            if (!res.ok) throw json;
            return json;
          });
        })
        .then(function () {
          window.location.href = '/checkout';
        })
        .catch(function (err) {
          setLoading(false);
          showError('form', (err && err.description) || 'Could not add to cart. Please try again.');
        });
    }

    function setLoading(isLoading) {
      if (!submitBtn) return;
      submitBtn.disabled = isLoading;
      if (submitLabel) submitLabel.textContent = isLoading ? 'Please wait…' : 'Check out';
    }

    function fieldValue(name) {
      var el = form.querySelector('[name="' + name + '"]');
      return el ? el.value.trim() : '';
    }

    function showError(key, msg) {
      var el = root.querySelector('[data-cgc-error="' + key + '"]');
      if (!el) return;
      el.textContent = msg;
      el.hidden = false;
    }

    function clearError(key) {
      var el = root.querySelector('[data-cgc-error="' + key + '"]');
      if (!el) return;
      el.textContent = '';
      el.hidden = true;
    }

    function clearAllErrors() {
      root.querySelectorAll('[data-cgc-error]').forEach(function (el) {
        el.textContent = '';
        el.hidden = true;
      });
    }

    function isValidEmail(v) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v || '');
    }

    // Formats a "YYYY-MM-DDTHH:mm" wall-clock value + timezone label as a
    // human-friendly string for display (cart/checkout property, buyer
    // confirmation email) -- e.g. "10 Sep 2026, 5:57 PM (GMT+05:30) Chennai,
    // Kolkata, Mumbai, New Delhi". No timezone math needed here since the
    // value is already the customer's chosen local wall-clock time.
    function formatLocalDisplay(datetimeLocalValue, tzLabel) {
      var m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(datetimeLocalValue);
      if (!m) return datetimeLocalValue + (tzLabel ? ' (' + tzLabel + ')' : '');

      var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      var year = m[1];
      var month = months[parseInt(m[2], 10) - 1];
      var day = parseInt(m[3], 10);
      var hour24 = parseInt(m[4], 10);
      var minute = m[5];
      var period = hour24 >= 12 ? 'PM' : 'AM';
      var hour12 = hour24 % 12;
      if (hour12 === 0) hour12 = 12;

      var dateStr = day + ' ' + month + ' ' + year + ', ' + hour12 + ':' + minute + ' ' + period;
      return tzLabel ? dateStr + ' (' + tzLabel + ')' : dateStr;
    }

    // Interprets a "YYYY-MM-DDTHH:mm" wall-clock value as local time in the
    // timezone whose UTC offset (in minutes, e.g. IST = 330) is given, and
    // returns the corresponding instant in UTC milliseconds.
    function localWallClockToUtcMs(datetimeLocalValue, offsetMinutes) {
      var m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(datetimeLocalValue);
      if (!m) return null;
      var asIfUtcMs = Date.UTC(
        parseInt(m[1], 10),
        parseInt(m[2], 10) - 1,
        parseInt(m[3], 10),
        parseInt(m[4], 10),
        parseInt(m[5], 10)
      );
      return asIfUtcMs - offsetMinutes * 60 * 1000;
    }
  }
})();
